'use client';

import { useCallback, useContext, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { AppContext } from '@/src/context/AppContext';
import {
  clearPendingCashier,
  PENDING_CASHIER_STORAGE_KEY,
  readPendingCashier,
  writePendingCashier,
} from '@/src/lib/pendingCashier';
import { getEmailVerificationStatus } from '@/src/lib/emailVerification';
import CouponErrorModal from '@/src/components/couponErrorModal/CouponErrorModal';

const VERIFY_MESSAGE =
  'Please verify your email address before claiming this promotion. Your selected promotion will remain available after verification.';

const PROFILE_STATUS_MESSAGE =
  'We could not confirm your account status right now. Please refresh the page and try again. Your selected promotion will remain available.';

/** Survives Strict Mode double-invoke within the same JS realm. */
let openInFlightKey = null;
let authPromptedKey = null;
let verifyNoticeKey = null;
let profileHydrateAttemptKey = null;
let profileStatusNoticeKey = null;

function getUserSessionKey(user) {
  if (!user) return null;
  return (
    user.username ||
    user.userName ||
    user.email ||
    user.id ||
    'session'
  );
}

export default function CouponCashier({
  openCashier,
  Click_Login,
  Click_Signup,
}) {
  const [verifyNoticeOpen, setVerifyNoticeOpen] = useState(false);
  const [verifyNoticeTitle, setVerifyNoticeTitle] = useState(
    'Email Verification Required',
  );
  const [verifyNoticeMessage, setVerifyNoticeMessage] =
    useState(VERIFY_MESSAGE);

  const {
    user,
    isLoadingUser,
    isRefreshingUserProfile,
    refreshUserProfile,
  } = useContext(AppContext);

  // Reactive (next/navigation), not window.location — this is what lets a
  // same-page URL update (e.g. a claim click on /promo/affiliate) re-trigger
  // the effect below without a full navigation/redirect. See PromoLandingPage.
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const removeCashierParams = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('coupon');
    params.delete('cashier');

    const nextSearch = params.toString();
    router.replace(`${pathname}${nextSearch ? `?${nextSearch}` : ''}`, {
      scroll: false,
    });
  }, [pathname, router, searchParams]);

  const resolveCashierTab = useCallback((cashierParam, coupon) => {
    // Deep-link with a specific coupon code → cashier coupon apply tab
    if (coupon) return '22';

    const value = String(cashierParam ?? '')
      .trim()
      .toLowerCase();

    // ?cashier=coupons → Coupons tab (same as openCashier("2") in BarTop)
    if (value === 'coupons' || value === 'coupon') return '2';

    // Allow numeric pass-through: ?cashier=1|2|3|…
    if (/^\d+$/.test(value)) return value;

    // Bare ?cashier or unknown value → default deposit tab
    return '0';
  }, []);

  const buildRequestFromUrl = useCallback(() => {
    const coupon = searchParams.get('coupon');
    const hasCashierParam = searchParams.has('cashier');
    const cashierParam = searchParams.get('cashier');
    const affid = searchParams.get('affid');

    if (!coupon && !hasCashierParam) return null;

    return {
      activeTab: resolveCashierTab(cashierParam, coupon),
      couponCode: coupon || null,
      authMode: affid && String(affid).trim() !== '' ? 'signup' : 'login',
      createdAt: Date.now(),
    };
  }, [searchParams, resolveCashierTab]);

  const requestKey = useCallback((request) => {
    if (!request) return null;
    return `${request.activeTab}|${request.couponCode || ''}|${request.authMode || ''}`;
  }, []);

  const showVerifyNoticeOnce = useCallback(
    (request) => {
      const key = requestKey(request);
      if (!key || verifyNoticeKey === key) return;
      verifyNoticeKey = key;
      setVerifyNoticeTitle('Email Verification Required');
      setVerifyNoticeMessage(VERIFY_MESSAGE);
      setVerifyNoticeOpen(true);
    },
    [requestKey],
  );

  const showProfileStatusNoticeOnce = useCallback(
    (request) => {
      const key = requestKey(request);
      if (!key || profileStatusNoticeKey === key) return;
      profileStatusNoticeKey = key;
      setVerifyNoticeTitle('Account status unavailable');
      setVerifyNoticeMessage(PROFILE_STATUS_MESSAGE);
      setVerifyNoticeOpen(true);
    },
    [requestKey],
  );

  const openAuthForRequest = useCallback(
    (request) => {
      const key = requestKey(request);
      if (!key || authPromptedKey === key) return;
      authPromptedKey = key;

      if (
        request?.authMode === 'signup' &&
        typeof Click_Signup === 'function'
      ) {
        Click_Signup();
        return;
      }

      if (typeof Click_Login === 'function') {
        Click_Login();
      }
    },
    [Click_Login, Click_Signup, requestKey],
  );

  const tryOpenCashier = useCallback(
    async (request) => {
      if (!request || typeof openCashier !== 'function') return false;

      const key = requestKey(request);
      if (!key) return false;
      if (openInFlightKey === key) return false;

      openInFlightKey = key;
      try {
        const opened = await openCashier(
          request.activeTab,
          request.couponCode || null,
        );

        if (opened) {
          clearPendingCashier();
          removeCashierParams();
          openInFlightKey = null;
          return true;
        }

        openInFlightKey = null;
        return false;
      } catch {
        openInFlightKey = null;
        return false;
      }
    },
    [openCashier, removeCashierParams, requestKey],
  );

  const hydrateProfileOnce = useCallback(
    async (userSessionKey) => {
      if (!userSessionKey) return null;
      if (profileHydrateAttemptKey === userSessionKey) return null;
      if (typeof refreshUserProfile !== 'function') return null;
      if (isRefreshingUserProfile) return null;

      profileHydrateAttemptKey = userSessionKey;
      try {
        return await refreshUserProfile({ replace: false });
      } catch {
        return null;
      }
    },
    [isRefreshingUserProfile, refreshUserProfile],
  );

  useEffect(() => {
    if (!user) {
      profileHydrateAttemptKey = null;
    }
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isLoadingUser) return;
    if (isRefreshingUserProfile) return;

    const urlRequest = buildRequestFromUrl();
    const pendingRequest = readPendingCashier();

    // URL request wins over stale pending
    if (urlRequest) {
      const saved = writePendingCashier(urlRequest);
      if (saved) {
        removeCashierParams();
      }
    }

    const activeRequest = urlRequest || pendingRequest;
    if (!activeRequest) return;

    if (!user) {
      openAuthForRequest(activeRequest);
      return;
    }

    // Allow a future guest session to re-prompt after this auth succeeds
    authPromptedKey = null;

    // Default ?cashier without coupon: no email gate
    if (!activeRequest.couponCode) {
      void tryOpenCashier(activeRequest);
      return;
    }

    const verificationStatus = getEmailVerificationStatus(user);

    if (verificationStatus === true) {
      void tryOpenCashier(activeRequest);
      return;
    }

    if (verificationStatus === false) {
      writePendingCashier(activeRequest);
      showVerifyNoticeOnce(activeRequest);
      return;
    }

    // null → unknown: hydrate full profile once, do not show verify modal yet
    writePendingCashier(activeRequest);
    const sessionKey = getUserSessionKey(user);

    if (profileHydrateAttemptKey === sessionKey) {
      // Already refreshed for this session and still unknown
      showProfileStatusNoticeOnce(activeRequest);
      return;
    }

    void hydrateProfileOnce(sessionKey);
  }, [
    user,
    isLoadingUser,
    isRefreshingUserProfile,
    buildRequestFromUrl,
    openAuthForRequest,
    removeCashierParams,
    showVerifyNoticeOnce,
    showProfileStatusNoticeOnce,
    tryOpenCashier,
    hydrateProfileOnce,
  ]);

  // Resume on focused tab when another tab updates pending storage
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onStorage = (event) => {
      if (event.key !== PENDING_CASHIER_STORAGE_KEY) return;
      if (document.visibilityState !== 'visible') return;
      if (
        typeof document.hasFocus === 'function' &&
        !document.hasFocus()
      ) {
        return;
      }
      if (!user || isLoadingUser || isRefreshingUserProfile) return;

      const pending = readPendingCashier();
      if (!pending) return;

      if (pending.couponCode) {
        const status = getEmailVerificationStatus(user);
        if (status !== true) return;
      }

      void tryOpenCashier(pending);
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user, isLoadingUser, isRefreshingUserProfile, tryOpenCashier]);

  return (
    <CouponErrorModal
      open={verifyNoticeOpen}
      onClose={() => {
        // Clear the dedupe guards so clicking the SAME coupon again
        // re-shows the notice instead of silently doing nothing — closing
        // the popup should not permanently suppress it for that request.
        verifyNoticeKey = null;
        profileStatusNoticeKey = null;
        setVerifyNoticeOpen(false);
      }}
      title={verifyNoticeTitle}
      genericMessage={verifyNoticeMessage}
    />
  );
}
